import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { SravniNewsSharedEnumsDictionaryType, SravniNewsSharedEnumsMaterialType } from '../__generated__/news/Api';
import { URL_TYPES } from '../constants/news';
import {
  IDictionaryDto,
  IExtraMaterial,
  IHighlightedMaterial,
  IMaterial,
  IRouterQuery,
  IRubricResultModel,
  TAuthorShort,
  THighLight,
  TMaterialHighlightsList,
  TPartnerMaterial,
  TRubricType,
  TTag,
} from '../interfaces/news';
import {
  getArticlesWithCommentsUserWantsToSee,
  getAuthors,
  getCurrentArticleIndex,
  getDictionaries,
  getListForUrl,
  getMaterials,
  getMaterialsList,
  getRubrics,
  getRubricsFromDictionary,
  getSingleMaterial,
  getTagsFromDictionary,
  getViewedArticles,
  selectAdditionalReadings,
  selectHighlightsList,
  selectPartnerMaterials,
  selectPopularMaterials,
  selectTagsByRubric,
} from '../selectors/news';

interface IGetRubricTypeParams {
  type?: string;
  rubricAlias: TRubricType;
  rubric: TRubricType;
  dictionaryRubrics: IDictionaryDto[];
  product: 'strakhovanie' | string;
}

export function useRubrics(): IRubricResultModel[] {
  return useSelector(getRubrics);
}

export function useTagsByRubric(rubric: string): TTag[] {
  return useSelector(selectTagsByRubric(rubric));
}

export function useDictionaries(): { [key in SravniNewsSharedEnumsDictionaryType]?: IDictionaryDto[] } {
  return useSelector(getDictionaries);
}

export function useTagsFromDictionary(): IDictionaryDto[] {
  return useSelector(getTagsFromDictionary);
}

export function useRubricsFromDictionary(): IDictionaryDto[] {
  return useSelector(getRubricsFromDictionary);
}

export function useMaterials(): IMaterial[] {
  return useSelector(getMaterials);
}

export function useSingleMaterial(): IMaterial {
  return useSelector(getSingleMaterial);
}

export function useCurrentMaterialIndex() {
  return useSelector(getCurrentArticleIndex);
}

export function useMaterialsList(): IHighlightedMaterial[] {
  return useSelector(getMaterialsList);
}

export function usePartnerMaterials(): { [key: string]: TPartnerMaterial } {
  return useSelector(selectPartnerMaterials);
}

export function useAdditionalReadings(): { [p: string]: IExtraMaterial } {
  return useSelector(selectAdditionalReadings);
}

export function useAuthors(): TAuthorShort[] {
  return useSelector(getAuthors);
}

export function usePopularMaterials(): THighLight[] {
  return useSelector(selectPopularMaterials);
}

export function useHighlightsList(): TMaterialHighlightsList {
  return useSelector(selectHighlightsList);
}

export const getRubricName = ({ rubricAlias, rubric, dictionaryRubrics, product }: IGetRubricTypeParams) => {
  if (product === 'strakhovanie') rubric = 'strahovki';
  return rubric === 'novosti'
    ? 'Новости'
    : dictionaryRubrics?.find(({ alias }) => [rubricAlias, rubric].includes(alias as TRubricType))?.name;
};

export const getRubricType = ({
  type,
  rubricAlias,
  rubric,
  dictionaryRubrics,
  product,
}: IGetRubricTypeParams): TRubricType => {
  if (product === 'strakhovanie') rubric = 'strahovki';
  return type === URL_TYPES[SravniNewsSharedEnumsMaterialType.News]
    ? ('novosti' as TRubricType)
    : (dictionaryRubrics?.find(({ alias }) => [rubricAlias, rubric].includes(alias as TRubricType))
        ?.value as TRubricType);
};

export const useRubricType = (): TRubricType => {
  const {
    query: { rubric, rubricAlias, type, product },
  } = useRouter() as TRouter<IRouterQuery>;

  const { rubrics: dictionaryRubrics } = useDictionaries();

  return getRubricType({ type, rubric, dictionaryRubrics, rubricAlias, product });
};

export const getTagType = ({ tag, tags }: { tag: string; tags: IDictionaryDto[] }) => {
  return tag ? tags.find(({ alias }) => tag === alias)?.value : undefined;
};

export const useTagType = () => {
  const {
    query: { tag },
  } = useRouter() as TRouter<IRouterQuery>;

  const { tags } = useDictionaries();

  return getTagType({ tag, tags });
};

export const useViewedArticles = () => useSelector(getViewedArticles);
export const useArticlesWithCommentsUserWantsToSee = () => useSelector(getArticlesWithCommentsUserWantsToSee);
export const useListForUrl = () => useSelector(getListForUrl);
